import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { getMember, deleteMember, getMemberPayments,
  createMemberContact,
  updateMemberContact,
  deleteMemberContact, createPayment, updatePayment, deletePayment } from '../../api/fpaMembers';
import { listWildfiresForMap } from '../../api/wildfires';
import { listLinkedParcelRefs, getTenantParcelByRef } from '../../api/parcels';
import { parcelToPolygons } from '@/utils/parcelGeometry';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import { MapCanvas } from '@/components/maps/MapCanvas';
import { useAuth } from '../../contexts/AuthContext';
import PaymentTimeline from '../../components/fpa/PaymentTimeline';
import PaymentDialog from '../../components/fpa/PaymentDialog';
import ContactDialog from '../../components/fpa/ContactDialog';
import ShareSheet from '../../components/common/ShareSheet';
import { buildMemberSharePayload } from '../../utils/memberShare';
import { parcelRecordCentroid } from '../../utils/memberParcelCentroid';

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`member-tabpanel-${index}`}
      aria-labelledby={`member-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const FPAMemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStaff, can } = useAuth();
  const canManageMember = can('fpa_member', 'update');

  const [member, setMember] = useState(null);
  const [payments, setPayments] = useState([]);
  const [offences, setOffences] = useState([]);
  const [parcelRef, setParcelRef] = useState(null);
  // The member's linked parcel (geometry + centroid), fetched by LISKEY so the
  // map can zoom to it. The purple highlight itself is driven by `parcelRef`.
  const [highlightParcel, setHighlightParcel] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentYear, setPaymentYear] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  // No fetch needed: this page already holds the hydrated member, so the
  // payload is built synchronously and the sheet's buttons remain the user
  // gesture navigator.share requires.
  const [shareOpen, setShareOpen] = useState(false);

  // Fetch member data
  useEffect(() => {
    const fetchMemberData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const memberData = await getMember(id);
        setMember(memberData);

        // Fetch payments
        try {
          const paymentsData = await getMemberPayments(id);
          setPayments(paymentsData.payments || paymentsData || []);
        } catch (err) {
          console.error('Error fetching payments:', err);
        }

        // Fetch this member's illegal-burn offences (fires linked to them).
        if (memberData.tenant_id) {
          try {
            const offencesData = await listWildfiresForMap({
              tenant_id: memberData.tenant_id,
              is_illegal: true,
              member_id: memberData.id,
            });
            setOffences(offencesData.wildfires || []);
          } catch (err) {
            console.error('Error fetching offences:', err);
          }
        }

        // Parcel link uses the cadastral LISKEY in the ParcelOwner table. Find
        // this member's linked ref, then fetch just that parcel's geometry so
        // the map can zoom to it and draw the purple highlight.
        if (memberData.tenant_id) {
          try {
            const linkData = await listLinkedParcelRefs(memberData.tenant_id);
            const match = (linkData?.links || []).find(
              (l) => l.member_id === memberData.id || l.member_id === Number(id)
            );
            if (match?.parcel_ref) {
              setParcelRef(match.parcel_ref);
              try {
                const parcelData = await getTenantParcelByRef(
                  memberData.tenant_id,
                  match.parcel_ref,
                );
                setHighlightParcel(parcelData);
              } catch (err) {
                console.error('Error fetching parcel geometry:', err);
              }
            }
          } catch (err) {
            console.error('Error fetching parcel links:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching member details:', error);
        setError('Failed to load member details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMemberData();
  }, [id]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle member deletion
  const handleDeleteMember = async () => {
    if (!window.confirm('Are you sure you want to delete this member?')) {
      return;
    }

    try {
      await deleteMember(id);
      navigate('/members?tab=fpa');
    } catch (error) {
      console.error('Error deleting member:', error);
      alert('Failed to delete member. Please try again later.');
    }
  };

  // Handle record payment
  const handleRecordPayment = (year) => {
    setPaymentYear(year);
    setEditingPayment(null);
    setPaymentDialogOpen(true);
  };

  // Handle edit payment
  const handleEditPayment = (payment) => {
    setPaymentYear(payment.year);
    setEditingPayment(payment);
    setPaymentDialogOpen(true);
  };

  // Handle save payment
  const handleSavePayment = async (paymentData) => {
    try {
      if (editingPayment) {
        await updatePayment(id, editingPayment.id, paymentData);
      } else {
        await createPayment(id, paymentData);
      }

      // Refresh payments
      const paymentsData = await getMemberPayments(id);
      setPayments(paymentsData.payments || paymentsData || []);
    } catch (error) {
      console.error('Error saving payment:', error);
      throw error;
    }
  };

  // Contacts. Deliberately the per-contact endpoints, not PUT
  // /fpa-members/{id}: that route reconciles the whole contacts array and
  // deletes anything missing from it, so a single-contact payload from a
  // quick-add dialog would silently wipe the member's other contacts.
  const handleAddContact = () => {
    setEditingContact(null);
    setContactDialogOpen(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setContactDialogOpen(true);
  };

  const handleSaveContact = async (contactData) => {
    try {
      if (editingContact) {
        await updateMemberContact(id, editingContact.id, contactData);
      } else {
        await createMemberContact(id, contactData);
      }
      // Contacts live on the member record, so refetch it (payments have
      // their own endpoint; contacts don't).
      setMember(await getMember(id));
    } catch (error) {
      console.error('Error saving contact:', error);
      throw error;
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!contact?.id) return;
    if (!window.confirm(`Delete contact "${contact.name}"?`)) return;
    try {
      await deleteMemberContact(id, contact.id);
      setMember(await getMember(id));
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };
  // Handle delete payment
  const handleDeletePayment = async (payment) => {
    if (!payment?.id) return;
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await deletePayment(id, payment.id);
      const paymentsData = await getMemberPayments(id);
      setPayments(paymentsData.payments || paymentsData || []);
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !member) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Member not found'}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to="/members?tab=fpa"
          >
            Back to Members
          </Button>
        </Box>
      </Container>
    );
  }

  // Determine estate billing mode
  const estateBilling = member.estate && member.estate.billing_mode === 'estate_pays';

  // Map framing for the linked parcel. `parcelToPolygons` turns the fetched
  // GeoJSON into Leaflet [lat,lng] rings; `fitBoundsTo` then zooms the map onto
  // the parcel. The purple highlight is applied by the tile renderer via
  // `highlightRef`. Falls back to the parcel centroid (or a default) for the
  // initial centre while bounds are computed.
  const parcelPolygons = highlightParcel ? parcelToPolygons(highlightParcel) : [];
  const fitBoundsTo =
    parcelPolygons.length > 0
      ? { polygons: parcelPolygons.map((p) => ({ positions: p.positions })) }
      : false;
  const mapCenter =
    highlightParcel?.centroid_lat != null && highlightParcel?.centroid_lon != null
      ? [highlightParcel.centroid_lat, highlightParcel.centroid_lon]
      : [-25.6429, 28.3434];

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              component={RouterLink}
              to="/members?tab=fpa"
              sx={{ mb: 2 }}
            >
              Back to Members
            </Button>

            <Typography variant="h4" component="h1" gutterBottom>
              {member.name}
              {member.ref_number && (
                <Chip
                  label={`#${member.ref_number}`}
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
              {member.estate_name && (
                <Chip
                  label={member.estate_name}
                  color="secondary"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
              {member.member_since && (
                <Chip
                  label={`Member since ${new Date(member.member_since).getFullYear()}`}
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* Outside the isStaff gate: the route is already behind
                fpa_member.read, and anyone who may view a member may pass
                its details to someone heading out to the property. */}
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={() => setShareOpen(true)}
            >
              Share
            </Button>
            {isStaff() && (
              <>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<EditIcon />}
                component={RouterLink}
                to={`/members/fpa/edit/${member.id}`}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteMember}
              >
                Delete
              </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Tabs — scrollable on mobile so 4 tabs don't get crushed */}
        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ '& .MuiTab-root': { minWidth: { xs: 90, md: 120 } } }}
          >
            <Tab label="Overview" />
            <Tab label="Contacts" />
            <Tab label="Payments" />
            <Tab label={offences.length > 0 ? `Offences (${offences.length})` : 'Offences'} />
            <Tab label="Map" />
          </Tabs>

          {/* Overview Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              {/* Property Info Card */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Property Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Plot Number:</strong> {member.plot_number || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Street:</strong> {member.street || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Area / Suburb:</strong> {member.area_suburb || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Size:</strong> {member.size_hectares ? `${member.size_hectares} hectares` : 'N/A'}
                    </Typography>
                    {member.estate_name && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Estate:</strong> {member.estate_name}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Hazards Card */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Hazards
                    </Typography>
                    {member.hazard_tags && member.hazard_tags.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {member.hazard_tags.map((hazard) => (
                          <Chip
                            key={hazard}
                            label={hazard.replace(/_/g, ' ')}
                            size="small"
                            color="warning"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hazards recorded
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Animals Card */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Animals
                    </Typography>
                    {member.animal_tags && member.animal_tags.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {member.animal_tags.map((animal) => (
                          <Chip
                            key={animal}
                            label={animal}
                            size="small"
                            color="info"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No animals recorded
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Notes */}
              {member.notes && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {member.notes}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Contacts Tab */}
          <TabPanel value={tabValue} index={1}>
            {canManageMember && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddContact}>
                  Add contact
                </Button>
              </Box>
            )}
            {member.contacts && member.contacts.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      {canManageMember && <TableCell align="right" sx={{ width: 96 }} />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {member.contacts.map((contact, index) => (
                      <TableRow key={contact.id ?? index}>
                        <TableCell>{contact.name || '-'}</TableCell>
                        <TableCell>
                          {contact.phone ? (
                            <Link href={`tel:${contact.phone}`} underline="hover">{contact.phone}</Link>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {contact.email ? (
                            <Link href={`mailto:${contact.email}`} underline="hover">{contact.email}</Link>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{contact.role || '-'}</TableCell>
                        {canManageMember && (
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title="Edit contact">
                              <IconButton size="small" onClick={() => handleEditContact(contact)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete contact">
                              <IconButton size="small" color="error" onClick={() => handleDeleteContact(contact)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No contacts recorded for this member.</Alert>
            )}
          </TabPanel>

          {/* Payments Tab */}
          <TabPanel value={tabValue} index={2}>
            <PaymentTimeline
              payments={payments}
              memberSince={member.member_since}
              onRecordPayment={handleRecordPayment}
              onEditPayment={handleEditPayment}
              onDeletePayment={handleDeletePayment}
              estateBilling={estateBilling}
              canManage={canManageMember}
            />
          </TabPanel>

          {/* Offences Tab — illegal burns logged against this member */}
          <TabPanel value={tabValue} index={3}>
            {offences.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offences.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell>
                          {o.start_time ? new Date(o.start_time).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>{o.fire_reference || `#${o.id}`}</TableCell>
                        <TableCell>{o.address || o.suburb || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={o.review_status === 'draft' ? 'Draft' : 'Submitted'}
                            size="small"
                            color={o.review_status === 'draft' ? 'default' : 'warning'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            component={RouterLink}
                            to={`/wildfires/${o.id}`}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="success">
                No illegal-burn offences recorded for this member.
              </Alert>
            )}
          </TabPanel>

          {/* Map Tab */}
          <TabPanel value={tabValue} index={4}>
            {parcelRef ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Linked parcel: <strong>{parcelRef}</strong> — shown with a purple outline on the map below.
                </Alert>
                <Box sx={{ height: 400, width: '100%' }}>
                  {/* The parcel lives in the tenant tile set; the canvas overlay
                      draws it with a purple outline (blue fill) via highlightRef,
                      and fitBoundsTo zooms the map onto it. */}
                  <MapCanvas
                    center={mapCenter}
                    zoom={15}
                    variant="inline"
                    gearMode="full"
                    forceParcels
                    mapSearch
                    tenantId={member.tenant_id}
                    highlightRef={parcelRef}
                    fitBoundsTo={fitBoundsTo}
                    scrollWheelZoom
                    className="!h-full !min-h-0"
                  />
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                No parcel linked to this member. Use the Parcel Map to link a parcel.
              </Alert>
            )}
          </TabPanel>
        </Paper>
      </Box>

      {/* Payment Dialog */}
      <ShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        // `highlightParcel` is the member's linked parcel, already fetched
        // above to frame the map — its centroid is the share's location too.
        payload={
          member
            ? buildMemberSharePayload(member, parcelRecordCentroid(highlightParcel))
            : null
        }
      />

      <ContactDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        onSave={handleSaveContact}
        contact={editingContact}
      />

      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onSave={handleSavePayment}
        year={paymentYear}
        payment={editingPayment}
      />
    </Container>
  );
};

export default FPAMemberDetail;
